import React from 'react';
import { CalendarClock, CircleDollarSign, ImageIcon, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLandingCurrency } from '../hooks/useLandingCurrency';
import type { LandingCurrency } from '../lib/landingCurrency';

type LandingPricingSectionProps = {
    /** Storybook and tests can force EUR or USD without geo lookup. */
    currency?: LandingCurrency;
};

type PricingCopy = {
    welcomeGiftHighlight: string;
    welcomeGiftDetail: string;
    factPerImage: string;
    factNoSubscription: string;
    factValidYear: string;
    factTopUp: string;
    walletLabel: string;
    walletBalanceLabel: string;
};

const sectionShellClass =
    'bg-linear-to-br from-slate-100 via-white to-slate-200 px-4 py-20 transition-colors dark:from-slate-950 dark:via-slate-950 dark:to-slate-900';

const hoverLiftClass =
    'transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0';

function usePricingCopy(currency: LandingCurrency): PricingCopy {
    const { t } = useTranslation();
    const currencyKey = currency === 'EUR' ? 'eur' : 'usd';

    return {
        welcomeGiftHighlight: t(`landingPricing.${currencyKey}.welcomeGiftHighlight`),
        welcomeGiftDetail: t(`landingPricing.${currencyKey}.welcomeGiftDetail`),
        factPerImage: t(`landingPricing.${currencyKey}.factPerImage`),
        factNoSubscription: t(`landingPricing.${currencyKey}.factNoSubscription`),
        factValidYear: t(`landingPricing.${currencyKey}.factValidYear`),
        factTopUp: t(`landingPricing.${currencyKey}.factTopUp`),
        walletLabel: t('landingPricing.walletLabel'),
        walletBalanceLabel: t('landingPricing.walletBalanceLabel'),
    };
}

const LandingPricingSection: React.FC<LandingPricingSectionProps> = ({
    currency: currencyOverride,
}) => {
    const { t } = useTranslation();
    const { currency } = useLandingCurrency(currencyOverride);
    const copy = usePricingCopy(currency);
    const ledgerRows = [
        { icon: ImageIcon, label: copy.factPerImage },
        { icon: CircleDollarSign, label: copy.factNoSubscription },
        { icon: CalendarClock, label: copy.factValidYear },
        { icon: Wallet, label: copy.factTopUp },
    ];

    return (
        <section
            id="pricing"
            className={sectionShellClass}
            data-testid="landing-pricing-section"
            data-landing-currency={currency}
        >
            <div className="container mx-auto max-w-6xl">
                <div
                    className={`mx-auto max-w-lg rounded-[28px] border-2 border-emerald-600/80 bg-linear-to-b from-emerald-600 to-emerald-700 p-1 shadow-[0_28px_60px_-32px_rgba(5,150,105,0.55)] dark:border-emerald-400/60 ${hoverLiftClass}`}
                >
                    <div className="rounded-[24px] bg-white p-6 dark:bg-slate-950 sm:p-8">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    {copy.walletLabel}
                                </p>
                                <h2 className="mt-2 text-xl font-bold text-slate-900 dark:text-white">
                                    {t('landingPricing.title')}
                                </h2>
                            </div>
                            <Wallet className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                        </div>

                        <div
                            className="mt-8 rounded-2xl bg-emerald-50 px-5 py-6 dark:bg-emerald-950/40"
                            data-testid="landing-pricing-welcome-highlight"
                        >
                            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                                {copy.walletBalanceLabel}
                            </p>
                            <p className="mt-2 text-4xl font-black text-emerald-700 dark:text-emerald-200">
                                {copy.welcomeGiftHighlight}
                            </p>
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                                {copy.welcomeGiftDetail}
                            </p>
                        </div>

                        <ul className="mt-6 divide-y divide-slate-200 dark:divide-white/10">
                            {ledgerRows.map(({ icon: Icon, label }) => (
                                <li key={label} className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
                                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100 sm:text-base">
                                        {label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

LandingPricingSection.displayName = 'LandingPricingSection';

export default LandingPricingSection;
